import React, { useState } from 'react';
import { Trophy, Users, Calendar, MapPin, ExternalLink, Award, Tag, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { hackathons } from '../data/portfolioData';
import { useTranslation } from '../hooks/useTranslation';
import { usePortfolioTranslation } from '../hooks/usePortfolioTranslation';

const PhotoGallery = ({ photos }) => {
    const { t } = useTranslation();
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadedPhotos, setLoadedPhotos] = useState([]);

    // Filter photos and track which ones actually load
    React.useEffect(() => {
        if (!photos || photos.length === 0) {
            setLoadedPhotos([]);
            return;
        }

        const validPhotos = [];
        let loadedCount = 0;

        photos.forEach((photo, index) => {
            const img = new Image();
            img.onload = () => {
                validPhotos[index] = photo;
                loadedCount++;
                if (loadedCount === photos.length) {
                    setLoadedPhotos(validPhotos.filter(Boolean));
                }
            };
            img.onerror = () => {
                loadedCount++;
                if (loadedCount === photos.length) {
                    setLoadedPhotos(validPhotos.filter(Boolean));
                }
            };
            img.src = photo.url;
        });
    }, [photos]);

    if (!photos || photos.length === 0 || loadedPhotos.length === 0) return null;

    const openLightbox = (index) => {
        setSelectedPhotoIndex(index);
        setCurrentIndex(index);
    };

    const closeLightbox = () => {
        setSelectedPhotoIndex(null);
    };

    const navigatePhoto = (direction) => {
        if (direction === 'next') {
            setCurrentIndex((prev) => (prev + 1) % loadedPhotos.length);
        } else {
            setCurrentIndex((prev) => (prev - 1 + loadedPhotos.length) % loadedPhotos.length);
        }
    };



    const getGridLayout = () => {
        // Use consistent 3-column grid layout like IEEEXtreme
        return "grid-cols-3 max-w-md mx-auto";
    };

    return (
        <>
            {/* Photo Grid */}
            <div className="mt-4 mb-6 w-full overflow-hidden">
                <div className={`grid ${getGridLayout()} gap-2 max-h-48 w-full`}>
                    {loadedPhotos.slice(0, 4).map((photo, index) => (
                        <div
                            key={index}
                            className="relative group cursor-pointer overflow-hidden rounded-lg aspect-square w-full h-full min-h-0"
                            onClick={() => openLightbox(index)}
                        >
                            <img
                                src={photo.url}
                                alt={t('hackathonMemory')}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIvPgo8cGF0aCBkPSJNOSAxMmEzIDMgMCAxIDAgNi0zIDMgMyAwIDAgMC02IDNaIiBmaWxsPSIjNmI3MjgwIi8+CjxwYXRoIGQ9Im0xMiAxNS41IDMuNS0zLjVhMS4xIDEuMSAwIDAgMSAxLjYgMGwuOSAxVjE5YTEgMSAwIDAgMS0xIDFIOGExIDEgMCAwIDEtMS0xdi0zLjVsMS4xLTEuMWExLjEgMS4xIDAgMCAxIDEuNiAwTDEyIDE1LjVaIiBmaWxsPSIjNmI3MjgwIi8+Cjwvc3ZnPgo=';
                                }}
                            />
                            {index === 3 && loadedPhotos.length > 4 && (
                                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                    <span className="text-white text-lg font-bold">+{loadedPhotos.length - 4}</span>
                                </div>
                            )}

                        </div>
                    ))}
                </div>
                {loadedPhotos.length > 4 && (
                    <div className="mt-4 text-center">
                        <button
                            onClick={() => openLightbox(0)}
                            className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors inline-block"
                        >
                            {t('viewAllPhotos', { count: loadedPhotos.length })}
                        </button>
                    </div>
                )}
            </div>

            {/* Lightbox Modal */}
            {selectedPhotoIndex !== null && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
                    <div className="relative max-w-4xl max-h-full">
                        {/* Close Button */}
                        <button
                            onClick={closeLightbox}
                            className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Navigation Buttons */}
                        {loadedPhotos.length > 1 && (
                            <>
                                <button
                                    onClick={() => navigatePhoto('prev')}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    onClick={() => navigatePhoto('next')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </>
                        )}

                        {/* Image */}
                        <div className="relative">
                            <img
                                src={loadedPhotos[currentIndex].url}
                                alt={t('hackathonMemory')}
                                className="max-w-full max-h-[80vh] object-contain mx-auto"
                            />

                            {/* Photo counter */}
                            {loadedPhotos.length > 1 && (
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                                    {currentIndex + 1} of {loadedPhotos.length}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const CertificateViewer = ({ certificateUrl, isOpen, onClose }) => {
    const { t } = useTranslation();

    if (!isOpen || !certificateUrl) return null;

    const isImageFile = /\.(jpg|jpeg|png|gif|webp)$/i.test(certificateUrl);
    const isPdfFile = /\.pdf$/i.test(certificateUrl);

    return (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="relative max-w-5xl max-h-full w-full">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Content */}
                <div className="bg-white rounded-lg overflow-hidden max-h-[90vh]">
                    {isImageFile ? (
                        <img
                            src={certificateUrl}
                            alt="Certificate"
                            className="w-full h-auto max-h-[80vh] object-contain"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                    ) : isPdfFile ? (
                        <iframe
                            src={certificateUrl}
                            className="w-full h-[80vh] border-0"
                            title="Certificate PDF"
                        />
                    ) : (
                        <div className="p-8 text-center">
                            <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 mb-4">{t('certificateNotSupported')}</p>
                            <a
                                href={certificateUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                <span>Open Certificate</span>
                            </a>
                        </div>
                    )}

                    {/* Error fallback */}
                    <div className="hidden p-8 text-center">
                        <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">Could not load certificate</p>
                        <a
                            href={certificateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            <span>Open Certificate</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HackathonCard = ({ hackathon }) => {
    const { translateHackathon } = usePortfolioTranslation();
    const [showCertificate, setShowCertificate] = useState(false);

    const translatedHackathon = translateHackathon(hackathon.translationKey);
    const displayTitle = translatedHackathon?.title || hackathon.name;
    const displayDescription = translatedHackathon?.description || hackathon.description;

    return (
        <div className="group relative rounded-lg border border-gray-700 hover:border-red-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/20 overflow-hidden">
            {/* Cover Photo with Gradient Overlay */}
            {hackathon.coverPhoto && (
                <div className="absolute inset-0 z-0">
                    <img
                        src={hackathon.coverPhoto}
                        alt="Hackathon cover"
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity duration-300"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-900/60 via-gray-800/50 to-gray-900/60"></div>
                </div>
            )}

            {/* Content */}
            <div className="relative z-10 p-6 bg-gradient-to-br from-gray-900/20 to-gray-800/20 backdrop-blur-sm">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                            <img
                                src={hackathon.logo}
                                alt={`${hackathon.organizer} logo`}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div className="w-full h-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center hidden">
                                <Trophy className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white group-hover:text-red-400 transition-colors">
                                {displayTitle}
                            </h3>
                            <p className="text-gray-400 text-sm font-semibold">{hackathon.organizer}</p>
                        </div>
                    </div>

                    {hackathon.certificateUrl && (
                        <button
                            onClick={() => setShowCertificate(true)}
                            className="flex items-center space-x-1 text-red-400 hover:text-red-300 transition-colors text-sm font-medium group cursor-pointer"
                        >
                            <Award className="w-4 h-4" />
                            <span>Certificate</span>
                            <Award className="w-3 h-3 group-hover:scale-110 transition-transform" />
                        </button>
                    )}
                </div>

                {/* Details */}
                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-6 text-gray-300 text-sm">
                        <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-red-400" />
                            <span>{hackathon.date}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-red-400" />
                            <span>{hackathon.location}</span>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Award className="w-4 h-4 text-yellow-400" />
                        <span className="text-yellow-400 font-semibold text-sm">{hackathon.achievement}</span>
                    </div>

                    {/* Teammates */}
                    {hackathon.teammates && hackathon.teammates.length > 0 && (
                        <div className="flex items-center space-x-3">
                            <Users className="w-4 h-4 text-blue-400" />
                            <span className="text-blue-400 text-sm font-medium mr-2">Team:</span>
                            <div className="flex items-center space-x-2">
                                {hackathon.teammates.map((teammate, index) => (
                                    <div key={index} className="relative">
                                        <button
                                            onClick={() => window.open(teammate.linkedin, '_blank')}
                                            className="w-8 h-8 rounded-full border-2 border-blue-400 hover:border-blue-300 transition-all duration-200 hover:scale-110 cursor-pointer bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold peer"
                                        >
                                            {teammate.name.split(' ').map(n => n[0]).join('')}
                                        </button>
                                        {/* Tooltip */}
                                        <div
                                            className={`absolute bottom-full mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 peer-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap ${index === 0 ? 'left-0' :
                                                index === 1 ? 'left-1/2 transform -translate-x-1/2' :
                                                    index === 2 ? 'right-0' :
                                                        index === 3 ? 'left-0 -translate-x-2' :
                                                            'right-0 translate-x-2'
                                                }`}
                                            style={{ zIndex: 50 + index }}
                                        >
                                            {teammate.name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Project Name */}
                    {hackathon.projectName && (
                        <div className="flex items-center space-x-2">
                            <Trophy className="w-4 h-4 text-green-400" />
                            <span className="text-green-400 text-sm font-medium">
                                Project: {hackathon.projectName}
                            </span>
                        </div>
                    )}
                </div>

                {/* Description */}
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    {displayDescription}
                </p>

                {/* Photo Gallery */}
                <PhotoGallery photos={hackathon.photos} />

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                    {hackathon.tags.map((tag, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center space-x-1 px-2 py-1 bg-red-900/30 text-red-300 rounded-md text-xs font-medium border border-red-800/50"
                        >
                            <Tag className="w-3 h-3" />
                            <span>{tag}</span>
                        </span>
                    ))}
                </div>
            </div>

            {/* Certificate Viewer Modal */}
            <CertificateViewer
                certificateUrl={hackathon.certificateUrl}
                isOpen={showCertificate}
                onClose={() => setShowCertificate(false)}
            />
        </div>
    );
}; const Hackathons = () => {
    if (!hackathons || hackathons.length === 0) {
        return null;
    }

    return (
        <section id="hackathons" className="py-20 bg-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="flex items-center justify-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                            <Users className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="text-4xl font-bold text-white">Hackathons</h2>
                    </div>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                        Collaborative coding competitions and innovation challenges where I've applied my skills
                        to solve complex problems under time constraints.
                    </p>
                </div>

                {/* Hackathons Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {hackathons.map((hackathon, index) => (
                        <HackathonCard key={index} hackathon={hackathon} />
                    ))}
                </div>


            </div>
        </section>
    );
};

export default Hackathons;